import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, stripBasiqPrefix } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUpRight, ArrowDownRight, Search, CalendarIcon, Filter, X, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

type TaxFilter = 'all' | 'yes' | 'no';

export default function Transactions() {
  const { data } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [taxFilter, setTaxFilter] = useState<TaxFilter>('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [showFilters, setShowFilters] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (category !== 'all') count++;
    if (taxFilter !== 'all') count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [category, taxFilter, dateFrom, dateTo]);

  const usedCategories = useMemo(() => {
    const cats = new Set(data.transactions.map(t => t.category));
    return Array.from(cats).sort();
  }, [data.transactions]);

  const filtered = useMemo(() => {
    let list = [...data.transactions];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        stripBasiqPrefix(t.description).toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    }

    if (category !== 'all') {
      list = list.filter(t => t.category === category);
    }

    if (taxFilter === 'yes') {
      list = list.filter(t => t.taxDeductible);
    } else if (taxFilter === 'no') {
      list = list.filter(t => !t.taxDeductible);
    }

    if (dateFrom) {
      const fromStr = format(dateFrom, 'yyyy-MM-dd');
      list = list.filter(t => t.date >= fromStr);
    }
    if (dateTo) {
      const toStr = format(dateTo, 'yyyy-MM-dd');
      list = list.filter(t => t.date <= toStr);
    }

    return list.sort((a, b) => b.date.localeCompare(a.date));
  }, [data.transactions, search, category, taxFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setCategory('all');
    setTaxFilter('all');
    setDateFrom(undefined);
    setDateTo(undefined);
    setSearch('');
  };

  return (
    <div className="px-4 pt-6 pb-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Transactions</h1>
        <Button size="sm" onClick={() => navigate('/transactions/add')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>

      {/* Search bar */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search transactions..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Filter toggle */}
      <div className="mb-3 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-1.5"
        >
          <Filter className="h-3.5 w-3.5" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="default" className="ml-1 h-5 w-5 rounded-full p-0 text-[10px] flex items-center justify-center">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-muted-foreground">
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <Card className="mb-4">
          <CardContent className="grid gap-3 p-3">
            {/* Category */}
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {usedCategories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tax deductible */}
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Tax Deductible</label>
              <Select value={taxFilter} onValueChange={v => setTaxFilter(v as TaxFilter)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Tax Deductible Only</SelectItem>
                  <SelectItem value="no">Non-Deductible Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className={cn('w-full justify-start text-left text-sm font-normal', !dateFrom && 'text-muted-foreground')}>
                      <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                      {dateFrom ? format(dateFrom, 'dd/MM/yy') : 'Start'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className={cn('w-full justify-start text-left text-sm font-normal', !dateTo && 'text-muted-foreground')}>
                      <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                      {dateTo ? format(dateTo, 'dd/MM/yy') : 'End'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction list */}
      {filtered.length === 0 ? (
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            {data.transactions.length === 0 ? 'No transactions yet' : 'No matching transactions'}
          </p>
          <p className="text-sm text-muted-foreground">
            {data.transactions.length === 0 ? 'Tap + to add your first one' : 'Try adjusting your filters'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => (
            <Card key={t.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => navigate(`/transactions/${encodeURIComponent(t.id)}`)}>
              <CardContent className="flex items-center gap-3 p-3">
                <div className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-lg',
                  t.type === 'income' ? 'bg-success/10' : 'bg-destructive/10'
                )}>
                  {t.type === 'income'
                    ? <ArrowUpRight className="h-4 w-4 text-success" />
                    : <ArrowDownRight className="h-4 w-4 text-destructive" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{stripBasiqPrefix(t.description) || t.category}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.category} · {new Date(t.date).toLocaleDateString('en-AU')}
                    {t.taxDeductible && <span className="ml-1 text-primary">· Tax</span>}
                  </p>
                </div>
                <p className={cn(
                  'font-display font-semibold',
                  t.type === 'income' ? 'text-success' : 'text-destructive'
                )}>
                  {t.type === 'income' ? '+' : '-'}{formatAUD(t.amount)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
